import React, {
    useEffect, useState, useMemo, useCallback
} from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import ColumnSelector from '../../webcommon/columnselector/ColumnSelector';
import Grid from '../../webcommon/grid/Grid';
import { getFilters } from '../../webcommon/header/filterheader/selectors';
import moment from 'moment';
import styles from './ImpactDetailed.module.scss';
import GridHeader from '../../common/gridheader/GridHeader';
import ExpanderButton from '../../common/gridheader/expanderbuttons/ExpanderButton';
import {
    combinedColumnsSelector, getImpactDetailedRows, getEodDate, getLoading,
    getTrades, getOriginalTrades
} from './selectors';
import ExportAsCsvButton from '../../common/gridheader/exportbutton/ExportAsCsvButton';
import {
    csvExportEmpty, setAllExpanded, setImpactDetailedColumns,
    toggleRowExpanded, impactDetailedLoaded
} from './actions';
import gridStyles from './GridOverride.module.scss';
import RecalculateData from '../../common/gridheader/recalculatedata/RecalculateData';
import { submitBookingTrades } from '../impactsummary/tradepricedtoday/actions';
import { tradesSelectedAreDifferent } from '../../common/helpermethods/HelperMethods';
import * as appConstants from '../../common/constants';

const calculateCsvFilename = date => `IM_Impact_Detailed_${(date ? moment(date) : moment()).format(appConstants.dateFormat)}.csv`;

const getRowLevelClassName = (rowInfo) => {
    if (rowInfo.original.imModel) {
        if (rowInfo.original.titleRow === true) { return '-headerRow'; }
        if (rowInfo.original.level === 0) { return 'level-0'; }
        return '-noBackground';
    }
    return '';
};

const processDataForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const {
            imModel, silo, riskClass, senstivity, bucket, trades,
            imPledgeUsd, imReceiveUsd, imPledgeEur, imReceiveEur
        } = row;
        return {
            'IM Model': imModel,
            Silo: silo,
            'Risk Class': riskClass,
            Sensitivity: senstivity,
            Bucket: bucket,
            Trades: trades,
            'IM Pledge USD': imPledgeUsd,
            'IM Receive USD': imReceiveUsd,
            'IM Pledge EUR': imPledgeEur,
            'IM Receive EUR': imReceiveEur
        };
    };
    return rows.map(mapDataKeys);
};

const ImpactDetailed = (props) => {
    const {
        impactDetailedLoaded,
        toggleRowExpanded,
        submitBookingTrades,
        trades,
        columns
    } = props;
    useEffect(() => {
        impactDetailedLoaded();
    }, [impactDetailedLoaded]);

    const [allExpanded, setCurrentExpandedState] = useState(false);

    const toggleExpanded = () => {
        props.setAllExpanded(!allExpanded);
        setCurrentExpandedState(!allExpanded);
    };

    const setImpactDetailedColumns = (columns) => {
        const data = columns.map((column) => {
            const { id, visible } = column;
            return { id, visible };
        });
        props.setImpactDetailedColumns(data);
    };

    const onRowExpanderClick = useCallback(({ id }) => {
        toggleRowExpanded(id);
    }, [toggleRowExpanded]);

    const recalculateData = useCallback(() => {
        submitBookingTrades(trades);
    }, [trades, submitBookingTrades]);

    const changes = tradesSelectedAreDifferent(props.trades, props.originalTrades);
    const visibleColumns = useMemo(() => columns.filter(
        column => column.visible
    ), [columns]);

    return (
        <div className={ styles.impactDetailed }>
            <GridHeader changesInTradeSelection={ changes } title="IM Impact Detailed">
                {changes ? (
                    <> <RecalculateData recalculatePageData={ recalculateData } />
                        <div className={ styles.separator }>&nbsp;</div>
                    </>
                ) : null}
                <ExpanderButton
                    allExpanded={ allExpanded }
                    setAllExpanded={ toggleExpanded }
                />
                <div className={ styles.separator }>&nbsp;</div>
                <div className={ styles.columnSelectorRelative }>
                    <ColumnSelector
                        applyFilter={ setImpactDetailedColumns }
                        columns={ props.columns }
                    />
                </div>
                <div className={ styles.separator }>&nbsp;</div>
                <ExportAsCsvButton
                    csvData={ props.rows }
                    csvExportEmpty={ props.csvExportEmpty }
                    csvFilename={ calculateCsvFilename(props.eodDate) }
                    csvMapping={ processDataForCsvExport }
                />
            </GridHeader>

            {props.columns.length
                && (
                    <Grid
                        columns={ visibleColumns }
                        defaultColumnSpacing={ 2 }
                        getRowLevelClassName={ getRowLevelClassName }
                        loading={ props.loading }
                        onExpanderClick={ onRowExpanderClick }
                        rows={ props.rows }
                        styles={ gridStyles }
                    />
                )}
        </div>
    );
};

ImpactDetailed.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            id: PropTypes.string
        })
    ),
    csvExportEmpty: PropTypes.func.isRequired,
    eodDate: PropTypes.string,
    loading: PropTypes.bool,
    impactDetailedLoaded: PropTypes.func.isRequired,
    originalTrades: PropTypes.arrayOf(PropTypes.shape({})),
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    setAllExpanded: PropTypes.func.isRequired,
    setImpactDetailedColumns: PropTypes.func.isRequired,
    submitBookingTrades: PropTypes.func.isRequired,
    toggleRowExpanded: PropTypes.func.isRequired,
    trades: PropTypes.arrayOf(PropTypes.shape({}))
};

ImpactDetailed.defaultProps = {
    columns: [],
    eodDate: '',
    loading: false,
    originalTrades: [],
    rows: [],
    trades: []
};

const mapStateToProps = state => ({
    columns: combinedColumnsSelector(state),
    eodDate: getEodDate(getFilters(state, 'ImpactAnalysis')),
    loading: getLoading(state),
    rows: getImpactDetailedRows(state),
    originalTrades: getOriginalTrades(state),
    trades: getTrades(state)
});

const mapDispatchToProps = {
    csvExportEmpty,
    impactDetailedLoaded,
    setAllExpanded,
    setImpactDetailedColumns,
    toggleRowExpanded,
    submitBookingTrades
};

export default withRouter(connect(mapStateToProps,
    mapDispatchToProps)(ImpactDetailed));

export { ImpactDetailed as ImpactDetailedUnconnected };
